import {
    getPrimaryAnalysis,
} from "@mrflark/spl_sdk";
import type {
    AiAnalysisEntry,
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    Badge,
    Button,
    Modal,
} from "@mrflark/ui";
import {
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import {
    Crown,
    RefreshCw,
} from "lucide-preact";
import {
    useEffect,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

import {
    splClient,
} from "../../api/client.ts";

type AiAnalysisDetailModalProps = {
    isOpen: boolean;
    onClose: () => void;
    item: MediaItem | null;
    onReanalyze: (item: MediaItem) => void;
};

const ModelTabBar = ({
    models,
    activeModel,
    primaryModel,
    onSelect,
}: {
    models: string[];
    activeModel: string;
    primaryModel: string | null;
    onSelect: (model: string) => void;
}) => {
    const shortName = (model: string): string => {
        const parts = model.split("/");
        return parts[parts.length - 1];
    };

    return (
        <div className="flex gap-1 border-b border-border pb-2 mb-3 flex-wrap">
            {models.map((model) => (
                <button
                    type="button"
                    key={model}
                    onClick={() => onSelect(model)}
                    className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded-t transition-colors ${
                        model === activeModel
                            ? "bg-accent/20 text-accent border-b-2 border-accent font-medium"
                            : "text-text-muted hover:text-text-secondary hover:bg-surface-hover"
                    }`}
                >
                    {model === primaryModel && (
                        <Crown size={12} className="text-yellow-400" />
                    )}
                    {shortName(model)}
                </button>
            ))}
        </div>
    );
};

const AnalysisEntryDetail = ({
    entry,
    modelName,
    isPrimary,
    onSetPrimary,
    isSettingPrimary,
}: {
    entry: AiAnalysisEntry;
    modelName: string;
    isPrimary: boolean;
    onSetPrimary: () => void;
    isSettingPrimary: boolean;
}) => {
    return (
        <div className="space-y-4">
            {/* description */}
            {entry.description && (
                <p className="text-text-primary text-sm leading-relaxed">
                    {entry.description}
                </p>
            )}

            {/* confidence bar */}
            <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-text-secondary">
                    <span>Confidence</span>
                    <span className="font-medium">
                        {(entry.confidence * 100).toFixed(0)}%
                    </span>
                </div>
                <div className="w-full h-2 bg-bg-tertiary rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all ${
                            entry.confidence >= 0.8
                                ? "bg-green-500"
                                : entry.confidence >= 0.5
                                ? "bg-yellow-500"
                                : "bg-red-500"
                        }`}
                        style={{
                            width: `${entry.confidence * 100}%`,
                        }}
                    />
                </div>
            </div>

            {/* suggested tags */}
            {entry.suggested_tags && entry.suggested_tags.length > 0 && (
                <div className="space-y-1.5">
                    <span className="text-xs font-medium text-text-muted uppercase tracking-wider">
                        Suggested Tags
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                        {entry.suggested_tags.map((tag) => (
                            <Badge key={tag}>{tag}</Badge>
                        ))}
                    </div>
                </div>
            )}

            {/* detected objects */}
            {entry.objects && entry.objects.length > 0 && (
                <div className="space-y-1.5">
                    <span className="text-xs font-medium text-text-muted uppercase tracking-wider">
                        Detected Objects
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                        {entry.objects.map((obj) => (
                            <Badge key={obj}>{obj}</Badge>
                        ))}
                    </div>
                </div>
            )}

            {/* metadata row */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-text-muted pt-2 border-t border-border">
                <span>Model: {modelName}</span>
                {entry.analyzed_at && (
                    <span>
                        Analyzed: {new Date(entry.analyzed_at).toLocaleString()}
                    </span>
                )}
                {isPrimary && (
                    <span className="flex items-center gap-1 text-yellow-400">
                        <Crown size={10} />
                        Primary
                    </span>
                )}
            </div>

            {/* set as primary button */}
            {!isPrimary && (
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={onSetPrimary}
                    disabled={isSettingPrimary}
                >
                    <Crown size={14} className="mr-1" />
                    {isSettingPrimary ? "Setting..." : "Set as Primary"}
                </Button>
            )}
        </div>
    );
};

export const AiAnalysisDetailModal = ({
    isOpen,
    onClose,
    item,
    onReanalyze,
}: AiAnalysisDetailModalProps) => {
    const queryClient = useQueryClient();
    const [selectedModel, setSelectedModel] = useState<string | null>(null);
    const [imageUrl, setImageUrl] = useState<string | null>(null);

    const analyses = item?.ai_analyses;
    const modelKeys = analyses ? Object.keys(analyses) : [];
    const primaryResult = getPrimaryAnalysis(analyses);
    const primaryModel = primaryResult?.modelName ?? null;

    const activeModel = selectedModel && modelKeys.includes(selectedModel)
        ? selectedModel
        : primaryModel ?? modelKeys[0] ?? null;
    const activeEntry = activeModel ? analyses?.[activeModel] : undefined;

    const thumbnailKey = item?.thumbnail_url ?? item?.media_url;

    useEffect(() => {
        setImageUrl(thumbnailKey ?? null);
    }, [thumbnailKey]);

    // reset selected model when item changes
    useEffect(() => {
        setSelectedModel(null);
    }, [item?.id]);

    const setPrimaryMutation = useMutation({
        mutationFn: ({
            mediaId,
            modelName,
        }: { mediaId: string; modelName: string; }) =>
            splClient.setAiPrimary(mediaId, modelName),
        onSuccess: () => {
            toast.success("Primary model updated");
            queryClient.invalidateQueries({
                queryKey: ["aiAnalysisItems"],
            });
        },
        onError: (error: Error) =>
            toast.error(`Failed to set primary: ${error.message}`),
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="AI Analysis Detail"
            size="lg"
        >
            {!item
                ? <p className="text-text-secondary">No item selected.</p>
                : (
                    <div className="space-y-4">
                        {/* thumbnail + filename */}
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-24 h-24 bg-bg-tertiary rounded overflow-hidden">
                                {imageUrl
                                    ? (
                                        <img
                                            src={imageUrl}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    )
                                    : (
                                        <div className="w-full h-full flex items-center justify-center text-text-muted text-xs">
                                            No preview
                                        </div>
                                    )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-text-primary truncate">
                                    {item.id}
                                </p>
                                <p className="text-xs text-text-muted mt-1">
                                    {item.media_type} &middot;{" "}
                                    {modelKeys.length}{" "}
                                    analysis{modelKeys.length !== 1 ? "es" : ""}
                                </p>
                            </div>
                        </div>

                        {/* model tabs + content */}
                        {modelKeys.length === 0
                            ? (
                                <p className="text-text-secondary text-sm">
                                    No AI analysis available for this item.
                                </p>
                            )
                            : (
                                <>
                                    {modelKeys.length > 1 && activeModel && (
                                        <ModelTabBar
                                            models={modelKeys}
                                            activeModel={activeModel}
                                            primaryModel={primaryModel}
                                            onSelect={setSelectedModel}
                                        />
                                    )}
                                    {activeModel && activeEntry && (
                                        <AnalysisEntryDetail
                                            entry={activeEntry}
                                            modelName={activeModel}
                                            isPrimary={activeModel ===
                                                primaryModel}
                                            onSetPrimary={() =>
                                                setPrimaryMutation.mutate({
                                                    mediaId: item.id,
                                                    modelName: activeModel,
                                                })}
                                            isSettingPrimary={setPrimaryMutation
                                                .isPending}
                                        />
                                    )}
                                </>
                            )}

                        {/* re-analyze button */}
                        <div className="pt-2 border-t border-border">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => onReanalyze(item)}
                            >
                                <RefreshCw size={14} className="mr-1" />
                                Re-Analyze This Item
                            </Button>
                        </div>
                    </div>
                )}
        </Modal>
    );
};
